/**
 * Formatting Utilities
 * Following constitution requirements for ArbiWin platform
 *
 * This file provides consistent formatting functions for dates, numbers,
 * currency, and other data types used throughout the application.
 */

import { DATE_TIME, LANGUAGES } from '../constants';

/**
 * Date and Time Formatting
 */
export const dateFormatters = {
  /**
   * Format date for display based on language
   * @param {Date|string} date - Date to format
   * @param {string} language - Language code (bg/en)
   * @returns {string} Formatted date string
   */
  formatDate: function(date, language) {
    if (!date) return '';

    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';

    language = language || LANGUAGES.BULGARIAN;

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();

    if (language === LANGUAGES.BULGARIAN) {
      return day + '.' + month + '.' + year;
    } else {
      return month + '/' + day + '/' + year;
    }
  },

  /**
   * Format time for display
   * @param {Date|string} time - Time to format
   * @returns {string} Formatted time string (HH:mm)
   */
  formatTime: function(time) {
    if (!time) return '';

    const timeObj = time instanceof Date ? time : new Date(time);
    if (isNaN(timeObj.getTime())) return '';

    const hours = String(timeObj.getHours()).padStart(2, '0');
    const minutes = String(timeObj.getMinutes()).padStart(2, '0');

    return hours + ':' + minutes;
  },

  /**
   * Format datetime for display
   * @param {Date|string} datetime - DateTime to format
   * @param {string} language - Language code (bg/en)
   * @returns {string} Formatted datetime string
   */
  formatDateTime: function(datetime, language) {
    if (!datetime) return '';

    const dateObj = datetime instanceof Date ? datetime : new Date(datetime);
    if (isNaN(dateObj.getTime())) return '';

    const datePart = this.formatDate(dateObj, language);
    const timePart = this.formatTime(dateObj);

    return datePart + ' ' + timePart;
  },

  /**
   * Format relative time (e.g., "2 minutes ago")
   * @param {Date|string} datetime - DateTime to format
   * @param {string} language - Language code (bg/en)
   * @returns {string} Relative time string
   */
  formatRelativeTime: function(datetime, language) {
    if (!datetime) return '';

    const dateObj = datetime instanceof Date ? datetime : new Date(datetime);
    if (isNaN(dateObj.getTime())) return '';

    language = language || LANGUAGES.BULGARIAN;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (language === LANGUAGES.BULGARIAN) {
      if (diffMinutes < 1) return 'току-що';
      if (diffMinutes < 60) return diffMinutes + ' мин. назад';
      if (diffHours < 24) return diffHours + ' ч. назад';
      if (diffDays < 7) return diffDays + ' дни назад';
      return this.formatDate(dateObj, language);
    } else {
      if (diffMinutes < 1) return 'just now';
      if (diffMinutes < 60) return diffMinutes + ' min ago';
      if (diffHours < 24) return diffHours + ' hours ago';
      if (diffDays < 7) return diffDays + ' days ago';
      return this.formatDate(dateObj, language);
    }
  },

  /**
   * Format countdown timer
   * @param {number} seconds - Seconds remaining
   * @param {string} language - Language code (bg/en)
   * @returns {string} Countdown string
   */
  formatCountdown: function(seconds, language) {
    if (seconds <= 0) return '';

    language = language || LANGUAGES.BULGARIAN;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    const minutesStr = String(minutes).padStart(2, '0');
    const secondsStr = String(remainingSeconds).padStart(2, '0');

    return minutesStr + ':' + secondsStr;
  }
};

/**
 * Number and Currency Formatting
 */
export const numberFormatters = {
  /**
   * Format currency amount in Euro
   * @param {number} amount - Amount to format
   * @param {string} language - Language code (bg/en)
   * @param {number} decimals - Number of decimal places
   * @returns {string} Formatted currency string
   */
  formatCurrency: function(amount, language, decimals) {
    if (amount === null || amount === undefined || isNaN(amount)) return '';

    language = language || LANGUAGES.BULGARIAN;
    decimals = decimals !== undefined ? decimals : 2;

    const formattedNumber = this.formatNumber(amount, decimals);

    if (language === LANGUAGES.BULGARIAN) {
      return formattedNumber + ' €';
    } else {
      return formattedNumber + ' EUR';
    }
  },

  /**
   * Format percentage value
   * @param {number} value - Percentage value (0.05 = 5%)
   * @param {number} decimals - Number of decimal places
   * @returns {string} Formatted percentage string
   */
  formatPercentage: function(value, decimals) {
    if (value === null || value === undefined || isNaN(value)) return '';

    decimals = decimals !== undefined ? decimals : 2;
    const percentage = value * 100;

    return this.formatNumber(percentage, decimals) + '%';
  },

  /**
   * Format decimal number with locale-specific formatting
   * @param {number} number - Number to format
   * @param {number} decimals - Number of decimal places
   * @returns {string} Formatted number string
   */
  formatNumber: function(number, decimals) {
    if (number === null || number === undefined || isNaN(number)) return '';

    decimals = decimals !== undefined ? decimals : 2;

    // Use Bulgarian number formatting (space as thousands separator, comma as decimal)
    const parts = number.toFixed(decimals).split('.');
    let integerPart = parts[0];
    const decimalPart = parts[1];

    // Add thousands separators
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    if (decimals > 0 && decimalPart) {
      return integerPart + ',' + decimalPart;
    } else {
      return integerPart;
    }
  },

  /**
   * Format betting odds (coefficient)
   * @param {number} odds - Betting odds value
   * @returns {string} Formatted odds string
   */
  formatOdds: function(odds) {
    if (odds === null || odds === undefined || isNaN(odds)) return '';

    // Always show 2 decimal places for odds
    return this.formatNumber(odds, 2);
  },

  /**
   * Format margin percentage for surebet display
   * @param {number} margin - Margin value (0.025 = 2.5%)
   * @returns {string} Formatted margin string with color indicator
   */
  formatMargin: function(margin) {
    if (margin === null || margin === undefined || isNaN(margin)) return '';

    const percentage = this.formatPercentage(margin, 2);
    const colorClass = margin > 0 ? 'positive' : 'negative';

    return {
      text: percentage,
      colorClass: colorClass,
      isProfitable: margin > 0
    };
  },

  /**
   * Format file size
   * @param {number} bytes - File size in bytes
   * @param {string} language - Language code (bg/en)
   * @returns {string} Formatted file size string
   */
  formatFileSize: function(bytes, language) {
    if (bytes === 0) return '0 B';
    if (!bytes || isNaN(bytes)) return '';

    language = language || LANGUAGES.BULGARIAN;
    const units = language === LANGUAGES.BULGARIAN
      ? ['B', 'KB', 'MB', 'GB', 'TB']
      : ['B', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = bytes / Math.pow(1024, i);

    return this.formatNumber(size, 1) + ' ' + units[i];
  }
};

/**
 * Text Formatting Utilities
 */
export const textFormatters = {
  /**
   * Capitalize first letter of string
   * @param {string} str - String to capitalize
   * @returns {string} Capitalized string
   */
  capitalize: function(str) {
    if (!str || typeof str !== 'string') return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  },

  /**
   * Convert string to title case
   * @param {string} str - String to convert
   * @returns {string} Title case string
   */
  toTitleCase: function(str) {
    if (!str || typeof str !== 'string') return '';

    return str.toLowerCase().split(' ').map(function(word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  },

  /**
   * Truncate text with ellipsis
   * @param {string} text - Text to truncate
   * @param {number} maxLength - Maximum length
   * @param {string} suffix - Suffix to add (default: '...')
   * @returns {string} Truncated text
   */
  truncate: function(text, maxLength, suffix) {
    if (!text || typeof text !== 'string') return '';

    maxLength = maxLength || 100;
    suffix = suffix || '...';

    if (text.length <= maxLength) return text;

    return text.substring(0, maxLength - suffix.length) + suffix;
  },

  /**
   * Format team names for display
   * @param {string} teamOne - First team name
   * @param {string} teamTwo - Second team name
   * @param {string} separator - Separator (default: ' vs ')
   * @returns {string} Formatted match string
   */
  formatMatchup: function(teamOne, teamTwo, separator) {
    if (!teamOne || !teamTwo) return '';

    separator = separator || ' vs ';
    return teamOne + separator + teamTwo;
  },

  /**
   * Clean and format user input
   * @param {string} input - User input string
   * @returns {string} Cleaned input
   */
  cleanInput: function(input) {
    if (!input || typeof input !== 'string') return '';

    return input.trim().replace(/\s+/g, ' ');
  },

  /**
   * Format bookmaker name for display
   * @param {string} bookmakerId - Bookmaker identifier
   * @returns {string} Display name
   */
  formatBookmakerName: function(bookmakerId) {
    if (!bookmakerId) return '';

    // Map of bookmaker IDs to display names
    const bookmakerNames = {
      'betano': 'Betano',
      'bet365': 'Bet365',
      'winbet': 'Winbet',
      'betwild': 'Betwild',
      'slybet': 'Slybet',
      'bwin': 'Bwin',
      'efbet': 'Efbet',
      'inbet': 'Inbet',
      'alphawin': 'Alphawin',
      'sesame': 'Sesame',
      'elitbet': 'Elitbet',
      'bethub': 'Bethub',
      'bet': 'Bet.bg',
      'topwin': 'Topwin',
      'mrbit': 'MrBit',
      'admiralbet': 'Admiralbet',
      'bg8888': '8888.bg',
      'palmsbet': 'Palmsbet',
      'everbet': 'Everbet',
      'magicbet': 'Magicbet',
      'betvam': 'Betvam',
      'qbet': 'Qbet',
      'imperiabet': 'Imperiabet'
    };

    return bookmakerNames[bookmakerId] || this.capitalize(bookmakerId);
  }
};

/**
 * URL and Query Parameter Formatting
 */
export const urlFormatters = {
  /**
   * Build query string from object
   * @param {Object} params - Parameters object
   * @returns {string} Query string
   */
  buildQueryString: function(params) {
    if (!params || typeof params !== 'object') return '';

    const queryParts = [];

    Object.keys(params).forEach(function(key) {
      let value = params[key];
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value = value.join(',');
        }
        queryParts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
      }
    });

    return queryParts.length > 0 ? '?' + queryParts.join('&') : '';
  },

  /**
   * Parse query string to object
   * @param {string} queryString - Query string to parse
   * @returns {Object} Parameters object
   */
  parseQueryString: function(queryString) {
    if (!queryString || typeof queryString !== 'string') return {};

    const params = {};
    const query = queryString.replace(/^\?/, '');

    if (!query) return params;

    const pairs = query.split('&');
    pairs.forEach(function(pair) {
      const parts = pair.split('=');
      if (parts.length === 2) {
        const key = decodeURIComponent(parts[0]);
        let value = decodeURIComponent(parts[1]);

        // Handle comma-separated values
        if (value.includes(',')) {
          value = value.split(',');
        }

        params[key] = value;
      }
    });

    return params;
  },

  /**
   * Clean URL for display
   * @param {string} url - URL to clean
   * @returns {string} Cleaned URL
   */
  cleanUrl: function(url) {
    if (!url || typeof url !== 'string') return '';

    return url.trim().replace(/\/+$/, ''); // Remove trailing slashes
  }
};

/**
 * Error Message Formatting
 */
export const errorFormatters = {
  /**
   * Format API error for user display
   * @param {Object} error - Error object
   * @param {string} language - Language code (bg/en)
   * @returns {string} User-friendly error message
   */
  formatApiError: function(error, language) {
    if (!error) return '';

    language = language || LANGUAGES.BULGARIAN;

    // Default messages
    const defaultMessages = {
      bg: 'Възникна грешка. Моля опитайте отново.',
      en: 'An error occurred. Please try again.'
    };

    // Status-specific messages
    const statusMessages = {
      400: {
        bg: 'Невалидни данни. Моля проверете въведената информация.',
        en: 'Invalid data. Please check your input.'
      },
      401: {
        bg: 'Необходимо е да влезете в системата.',
        en: 'Please log in to continue.'
      },
      402: {
        bg: 'Необходим е активен абонамент за достъп до тази функция.',
        en: 'Active subscription required to access this feature.'
      },
      403: {
        bg: 'Нямате разрешение за достъп до тази функция.',
        en: 'You do not have permission to access this feature.'
      },
      404: {
        bg: 'Търсената информация не е намерена.',
        en: 'The requested information was not found.'
      },
      500: {
        bg: 'Сървърна грешка. Моля опитайте по-късно.',
        en: 'Server error. Please try again later.'
      }
    };

    // Use specific message if available
    if (error.status && statusMessages[error.status]) {
      return statusMessages[error.status][language];
    }

    // Use error message if provided
    if (error.message && typeof error.message === 'string') {
      return error.message;
    }

    // Fallback to default message
    return defaultMessages[language];
  },

  /**
   * Format validation errors
   * @param {Object} validationErrors - Validation errors object
   * @param {string} language - Language code (bg/en)
   * @returns {Array} Array of formatted error messages
   */
  formatValidationErrors: function(validationErrors, language) {
    if (!validationErrors || typeof validationErrors !== 'object') return [];

    language = language || LANGUAGES.BULGARIAN;
    const errors = [];

    Object.keys(validationErrors).forEach(function(field) {
      const fieldErrors = validationErrors[field];
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach(function(error) {
          errors.push(error);
        });
      } else if (typeof fieldErrors === 'string') {
        errors.push(fieldErrors);
      }
    });

    return errors;
  }
};

/**
 * Data Structure Formatting
 */
export const dataFormatters = {
  /**
   * Format surebet data for display
   * @param {Object} marketsAnalysisItem - MarketsAnalysisDTO item
   * @param {string} language - Language code (bg/en)
   * @returns {Object} Formatted surebet data
   */
  formatSurebetData: function(marketsAnalysisItem, language) {
    if (!marketsAnalysisItem || !marketsAnalysisItem.event) return null;

    language = language || LANGUAGES.BULGARIAN;
    const event = marketsAnalysisItem.event;

    return {
      id: marketsAnalysisItem.id,
      matchup: textFormatters.formatMatchup(event.teamOne, event.teamTwo),
      date: dateFormatters.formatDate(event.date, language),
      time: dateFormatters.formatTime(event.time),
      championship: event.championship,
      sport: event.sport,
      marginFlag: marketsAnalysisItem.marginFlag,
      margins: marketsAnalysisItem.margins || {},
      hasData: marketsAnalysisItem.marginFlag &&
               marketsAnalysisItem.margins &&
               Object.keys(marketsAnalysisItem.margins).length > 0
    };
  },

  /**
   * Format coefficient data for display
   * @param {Object} coefficient - Coefficient object
   * @returns {Object} Formatted coefficient data
   */
  formatCoefficientData: function(coefficient) {
    if (!coefficient) return null;

    return {
      bookmaker: textFormatters.formatBookmakerName(coefficient.bookmaker),
      bookmakerId: coefficient.bookmaker,
      type: coefficient.type,
      odds: numberFormatters.formatOdds(coefficient.value),
      rawValue: coefficient.value
    };
  },

  /**
   * Format margin data for display
   * @param {Object} margin - Margin object
   * @param {string} marginType - Margin type key
   * @returns {Object} Formatted margin data
   */
  formatMarginData: function(margin, marginType) {
    if (!margin) return null;

    const formattedMargin = numberFormatters.formatMargin(margin.value);
    let coefficients = [];

    if (margin.coefficients && Array.isArray(margin.coefficients)) {
      coefficients = margin.coefficients.map(function(coef) {
        return dataFormatters.formatCoefficientData(coef);
      }).filter(function(coef) {
        return coef !== null;
      });
    }

    return {
      type: marginType,
      value: margin.value,
      formattedValue: formattedMargin.text,
      colorClass: formattedMargin.colorClass,
      isProfitable: formattedMargin.isProfitable,
      coefficients: coefficients,
      hasCoefficients: coefficients.length > 0
    };
  }
};

/**
 * Input Sanitization and Formatting
 */
export const inputFormatters = {
  /**
   * Sanitize HTML content
   * @param {string} html - HTML content to sanitize
   * @returns {string} Sanitized HTML
   */
  sanitizeHtml: function(html) {
    if (!html || typeof html !== 'string') return '';

    // Basic HTML sanitization (remove script tags and dangerous attributes)
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '')
      .replace(/javascript:/gi, '');
  },

  /**
   * Format phone number
   * @param {string} phone - Phone number to format
   * @returns {string} Formatted phone number
   */
  formatPhone: function(phone) {
    if (!phone || typeof phone !== 'string') return '';

    // Remove non-digits
    const digits = phone.replace(/\D/g, '');

    // Bulgarian phone number format
    if (digits.length === 9 && digits.startsWith('8')) {
      return '+359 ' + digits.substring(1, 3) + ' ' + digits.substring(3, 6) + ' ' + digits.substring(6);
    }

    return phone; // Return original if can't format
  },

  /**
   * Format stake input (remove non-numeric characters except decimal)
   * @param {string} input - Stake input string
   * @returns {string} Cleaned stake input
   */
  formatStakeInput: function(input) {
    if (!input || typeof input !== 'string') return '';

    // Allow only numbers, comma, and dot
    let cleaned = input.replace(/[^0-9,.]/g, '');

    // Replace comma with dot for decimal separator
    cleaned = cleaned.replace(',', '.');

    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }

    return cleaned;
  }
};

/**
 * Export all formatters
 */
export default {
  date: dateFormatters,
  number: numberFormatters,
  text: textFormatters,
  url: urlFormatters,
  error: errorFormatters,
  input: inputFormatters,
  data: dataFormatters
};
